import "./form.scss";
import React, { useState, useEffect, useCallback, useRef } from "react";
import { useGoogleReCaptcha } from "react-google-recaptcha-v3";
import Input from "./input/input";
import Select from "./select/select";
import Textarea from "./textarea/textarea";
import Alert from "./alert/alert";
import LoadingSpinner from "./loading-spinner/loading-spinner";
import SubmitButton from "./submit-btn/submit-btn";
import {
  FORM_FIELDS_CONFIG,
  INITIAL_FORM_DATA,
  FIELD_OPTIONS_MAP,
  FORM_FIELDS_ORDER,
  QUESTION_MAP,
} from "./lib/constants";
import { validateFormData } from "./lib/utils";
import { API_ENDPOINTS, RECAPTCHA_CONFIG } from "./lib/config";
import { useAlert } from "./lib/useAlert";

const Form = () => {
  const [formData, setFormData] = useState(INITIAL_FORM_DATA);
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [globalID, setGlobalID] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const hasFetched = useRef(false);

  const { executeRecaptcha } = useGoogleReCaptcha();
  const { alert, showAlert, hideAlert } = useAlert();

  const fetchOrganizationSummary = useCallback(async () => {
    // Prevent multiple fetches
    if (hasFetched.current || globalID) return;

    hasFetched.current = true;

    try {
      const response = await fetch(API_ENDPOINTS.ORGANIZATION_SUMMARY, {
        method: "GET",
        headers: { "Content-Type": "application/json" },
      });

      if (!response.ok) {
        throw new Error(
          `Failed to fetch organization summary: ${response.status}`
        );
      }

      const data = await response.json();

      if (data._id) {
        setGlobalID(data._id);
      } else {
        throw new Error("Organization ID not found in response");
      }
    } catch (error) {
      console.error("Error fetching organization summary:", error);
      showAlert(
        "error",
        "Failed to load organization information. Please refresh the page."
      );
      hasFetched.current = false; // Reset on error to allow retry
    } finally {
      setIsLoading(false);
    }
  }, [globalID, showAlert]);

  useEffect(() => {
    fetchOrganizationSummary();
  }, [fetchOrganizationSummary]);

  const handleInputChange = (name, value) => {
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const prepareSurveyPayload = (formData, recaptchaToken) => {

    let code = ''
    if ( 'surveyCode' in formData ) {
      code = formData.surveyCode
      delete formData.surveyCode
    }

    const fields = Object.keys(formData).map((fieldName) => ({
      question: QUESTION_MAP[fieldName] || fieldName,
      answer: formData[fieldName] || "",
      answer_details: "",
    }));

    return {
      global_id: globalID,
      lang: "en",
      fields,
      code,
      captcha_token: recaptchaToken,
    };
  };

  const submitFormData = async (surveyPayload) => {
    const response = await fetch(API_ENDPOINTS.SURVEYS, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(surveyPayload),
    });

    if (!response.ok) {
      throw new Error(`Submission failed: ${response.status}`);
    }

    return await response.json();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    hideAlert();

    try {
      const validation = validateFormData(formData);

      if (!validation.isValid) {
        setErrors(validation.errors);
        const firstError = Object.values(validation.errors)[0];
        showAlert("error", firstError);
        return;
      }

      if (!globalID) {
        showAlert(
          "error",
          "Organization information not loaded. Please refresh the page."
        );
        return;
      }

      if (!executeRecaptcha) {
        showAlert(
          "error",
          "reCAPTCHA not ready. Please refresh the page and try again."
        );
        return;
      }

      const recaptchaToken = await executeRecaptcha(RECAPTCHA_CONFIG.ACTION);

      if (!recaptchaToken) {
        showAlert("error", "reCAPTCHA verification failed. Please try again.");
        return;
      }

      const surveyPayload = prepareSurveyPayload(formData, recaptchaToken);
      await submitFormData(surveyPayload);

      setFormData(INITIAL_FORM_DATA);
      setErrors({});
      showAlert(
        "success",
        "Thank you for your feedback! Your message has been sent."
      );
    } catch (error) {
      console.error("Submission error:", error);
      showAlert(
        "error",
        error.message ||
          "There was an error sending your message. Please try again."
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderField = (fieldName) => {
    const config = FORM_FIELDS_CONFIG[fieldName];
    const commonProps = {
      name: fieldName,
      label: config.label,
      value: formData[fieldName],
      error: errors[fieldName],
      required: config.required,
      onChange: handleInputChange,
      className: `form-field--${fieldName}`,
    };

    switch (config.type) {
      case "email":
      case "text":
        return (
          <Input
            key={fieldName}
            type={config.type}
            placeholder={config.placeholder}
            {...commonProps}
          />
        );
      case "textarea":
        return (
          <Textarea
            key={fieldName}
            placeholder={config.placeholder}
            rows={config.rows}
            {...commonProps}
          />
        );
      case "select":
        return (
          <Select
            key={fieldName}
            options={FIELD_OPTIONS_MAP[fieldName]}
            {...commonProps}
          />
        );
      default:
        return null;
    }
  };

  if (isLoading) {
    return (
      <div className="form container">
        <LoadingSpinner message="Loading organization information..." />
      </div>
    );
  }

  return (
    <div className="form container">
      <form className="form__container" onSubmit={handleSubmit}>
        <div className="form__fields">
          {FORM_FIELDS_ORDER.map((fieldName) => renderField(fieldName))}
        </div>

        <div className="form__submit d-flex flex-column align-items-end">
          <SubmitButton isSubmitting={isSubmitting} />

          <Alert
            type={alert.type}
            message={alert.message}
            show={alert.show}
            onClose={hideAlert}
            className="form__alert w-100"
          />
        </div>
      </form>
    </div>
  );
};

export default Form;
